import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FileTextIcon, ImageIcon, PdfIcon } from '../../../../components/Icons';
import {
  useRemoveKnowledgeFile,
  useRemoveUploadedFile,
} from '../../../../hooks/apiHooks/twinHooks';
import { useTwinData } from '../../../../hooks/storeHooks/useTwin';
import { IKnowledgeFile } from '../../../../store/reducers/twinReducer';
import { MemoryItemCard } from './MemoryItemCard';

interface IKnowledgeFileItemProps {
  storedFile: IKnowledgeFile;
  retryUpload: (file: File, retry: boolean) => void;
  mentorId: string;
  isReadyStatus: boolean;
  orgId: string;
  clearInput: () => void;
}

const getFileIcon = (fileName: string): React.ReactNode => {
  const extension = fileName.split('.').pop()?.toLowerCase();
  switch (extension) {
    case 'pdf':
      return <PdfIcon size={24} />;
    case 'doc':
    case 'docx':
    case 'txt':
    case 'md':
      return <FileTextIcon />;
    case 'png':
    case 'jpeg':
    case 'jpg':
      return <ImageIcon size={24} />;
    default:
      return <FileTextIcon />;
  }
};

const KnowledgeFileItem: React.FC<IKnowledgeFileItemProps> = memo(
  ({ retryUpload, storedFile, mentorId, isReadyStatus, orgId, clearInput }) => {
    const { t } = useTranslation();
    const { mutateAsync: removeUploadedFile } = useRemoveUploadedFile();
    const { mutateAsync: removeKnowledgeFileAPI } = useRemoveKnowledgeFile();
    const { removeKnowledgeFile, updateKnowledgeFileStatus } = useTwinData();
    const handleRetry = () => {
      if (!storedFile.file) return;
      retryUpload(storedFile.file, true);
    };

    const handleDelete = () => {
      if (storedFile.status === 'uploaded') {
        updateKnowledgeFileStatus({
          ...storedFile,
          status: 'uploading',
        });
        if (isReadyStatus) {
          removeKnowledgeFileAPI({
            uploadId: storedFile.id,
            mentorId,
          })
            .then(() => {
              removeKnowledgeFile(storedFile.fileName);
              toast.success(t('twin.fileDeleted'));
            })
            .catch(() => {
              updateKnowledgeFileStatus({
                ...storedFile,
                status: 'uploaded',
              });
              toast.error(t('twin.fileDeleteFailed'));
            })
            .finally(() => {
              clearInput();
            });
          return;
        }

        removeUploadedFile({
          id: storedFile.id,
          organization_id: orgId,
        })
          .then(() => {
            removeKnowledgeFile(storedFile.fileName);
          })
          .catch(() => {
            updateKnowledgeFileStatus({
              ...storedFile,
              status: 'uploaded',
            });
            toast.error(t('twin.fileDeleteFailed'));
          })
          .finally(() => {
            clearInput();
          });
      }
    };

    const processingStatus = useMemo(() => {
      switch (storedFile.status) {
        case 'uploading':
          return 'processing';
        case 'uploaded':
          return 'idle';
        case 'error':
          return 'error';
        default:
          return 'none';
      }
    }, [storedFile.status]);

    return (
      <MemoryItemCard
        icon={getFileIcon(storedFile.fileName)}
        title={storedFile.fileName}
        status={processingStatus}
        onDelete={handleDelete}
        onRetry={handleRetry}
        deleteDisabled={storedFile.status === 'uploading'}
        oneLine
      />
    );
  },
);

export { KnowledgeFileItem };
